// Services/CodingAIService.js
import { endpoints, baseurl } from "../Utils/Constants";

const parseSseEvent = (rawEvent) => {
    const lines = rawEvent.split(/\r?\n/);
    let eventType = "message";
    const dataLines = [];

    for (const line of lines) {
        if (line.startsWith("event:")) {
            eventType = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).replace(/^ /, ""));
        }
    }

    return { eventType, data: dataLines.join("\n") };
};

export const streamMedicalCodes = async (clinicalText, token, ids = {}, onAck, onResult, onPhiDetected, onError, onProgress) => {
    const handleEvent = (eventType, data) => {
        if (eventType === "ack") {
            if (typeof onAck === "function") onAck(data);
        } else if (eventType === "progress") {
            if (typeof onProgress === "function") onProgress();
        } else if (eventType === "phi_detected") {
            if (typeof onPhiDetected !== "function") return;
            try {
                const parsed = JSON.parse(data);
                onPhiDetected(parsed);
            } catch (err) {
                console.warn("Ignoring malformed phi_detected event:", err);
            }
        } else if (eventType === "result") {
            if (typeof onResult !== "function") return;
            try {
                const parsed = JSON.parse(data);
                onResult(parsed);
            } catch (err) {
                console.warn("Ignoring malformed result event:", err);
            }
        }
    };

    try {
        const response = await fetch(baseurl + endpoints.CODING_AI_GENERATE_CODES, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                clinical_text: clinicalText,
                session_id: ids.sessionId,
                chat_id: ids.chatId,
                // Only sent by super admins / internal users; the API scopes external users to their token's organization.
                organization_id: ids.organizationId || undefined,
            }),
        });

        const contentType = response.headers.get("content-type") || "";

        if (!contentType.includes("text/event-stream")) {
            const json = await response.json().catch(() => null);
            const findings = json?.data?.phi_detected;

            if (json?.status === "PHIDetected" || (Array.isArray(findings) && findings.length > 0)) {
                if (typeof onPhiDetected === "function") {
                    onPhiDetected({ message: json?.message, phi_detected: findings || [] });
                }
                return { is_success: false, message: json?.message || "Protected health information was detected." };
            }

            if (!response.ok || json?.is_success === false) {
                const message = json?.message || "Failed to generate codes";
                if (typeof onError === "function") onError(message);
                return { is_success: false, message };
            }

            if (typeof onResult === "function") onResult(json?.data ?? json);
            return { is_success: true, message: json?.message || "" };
        }

        if (!response.body) {
            const text = await response.text();
            return {
                is_success: response.ok,
                message: text || (response.ok ? "" : "Failed to generate codes"),
            };
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let streamError = null;
        let gotResult = false;
        let gotPhi = false;

        const dispatch = (eventType, data) => {
            if (eventType === "error") {
                if (data) streamError = data;
            } else if (eventType === "result") {
                gotResult = true;
                handleEvent(eventType, data);
            } else if (eventType === "phi_detected") {
                gotPhi = true;
                handleEvent(eventType, data);
            } else if (eventType === "progress" || eventType === "ack") {
                handleEvent(eventType, data);
            } else if (data) {
                handleEvent(eventType, data);
            }
        };

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            let boundary = buffer.indexOf("\n\n");
            while (boundary !== -1) {
                const { eventType, data } = parseSseEvent(buffer.slice(0, boundary));
                buffer = buffer.slice(boundary + 2);
                dispatch(eventType, data);
                boundary = buffer.indexOf("\n\n");
            }
        }

        if (buffer.trim()) {
            const { eventType, data } = parseSseEvent(buffer);
            dispatch(eventType, data);
        }

        if (streamError) {
            if (typeof onError === "function") onError(streamError);
            return { is_success: false, message: streamError };
        }
        if (!response.ok || (!gotResult && !gotPhi)) {
            const message = "Failed to generate codes";
            if (typeof onError === "function") onError(message);
            return { is_success: false, message };
        }
        return { is_success: true, message: "" };
    } catch (error) {
        console.error("Error streaming Coding AI codes:", error);
        const message = error.message || "Failed to generate codes";
        if (typeof onError === "function") onError(message);
        return { is_success: false, message };
    }
};
